import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

interface ScrollImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  containerProps?: HTMLAttributes<HTMLDivElement>
  scrollSpeed?: number
  resetDuration?: number
}

export default function ScrollImage({
  containerProps,
  scrollSpeed = 1,
  resetDuration = 1.5,
  ...imgProps
}: ScrollImageProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const directionRef = useRef<1 | -1>(-1)
  const posRef = useRef(0)
  const [canScroll, setCanScroll] = useState(false)

  useEffect(() => {
    const img = imgRef.current
    const container = containerRef.current
    if (!img || !container) return

    const checkDimensions = () => {
      setCanScroll(img.clientHeight > container.clientHeight)
    }

    const resizeObserver = new ResizeObserver(checkDimensions)
    resizeObserver.observe(img)
    resizeObserver.observe(container)

    if (img.complete) checkDimensions()
    else img.addEventListener('load', checkDimensions)

    return () => {
      img.removeEventListener('load', checkDimensions)
      resizeObserver.disconnect()
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [imgProps.src])

  const step = () => {
    const img = imgRef.current
    const container = containerRef.current
    if (!img || !container) return

    const containerHeight = container.clientHeight
    const maxScroll = img.clientHeight - containerHeight

    posRef.current += directionRef.current * scrollSpeed

    if (posRef.current <= -maxScroll) {
      directionRef.current = 1
    } else if (posRef.current >= 0) {
      directionRef.current = -1
    }

    img.style.transform = `translateY(${posRef.current}px)`
    animationRef.current = requestAnimationFrame(step)
  }

  const handlePointerEnter = () => {
    if (!canScroll || !imgRef.current || animationRef.current) return

    imgRef.current.style.transition = 'none'
    animationRef.current = requestAnimationFrame(step)
  }

  const handlePointerLeave = () => {
    if (!imgRef.current) return

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }

    imgRef.current.style.transition = `transform ${resetDuration}s ease-in-out`
    imgRef.current.style.transform = 'translateY(0)'
    posRef.current = 0
    directionRef.current = -1
  }

  return (
    <div
      ref={containerRef}
      {...containerProps}
      className={twMerge('overflow-hidden', containerProps?.className)}
    >
      <img
        ref={imgRef}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        {...imgProps}
        className={twMerge('h-auto w-full transition-all duration-300', imgProps?.className)}
      />
    </div>
  )
}
